import React, { useEffect, useState } from "react";
import { styled, SxProps } from "@mui/material/styles";
import { EmtTypography } from "./EmtTypography";
import FormHelperText from "@mui/material/FormHelperText";
import Box from "@mui/material/Box";
import { EmtTheme } from "../types";
import { useMobile } from "../hooks/useMobile";

export type EmtTextareaProps = {
  style?: React.CSSProperties;
  /**
   * Like placeholder
   */
  label?: string;
  /**
   * If maxLength is set, show counter on top right of the field
   */
  maxLength?: number;
  /**
   * @default undefined
   */
  value?: string;
  /**
   * @default undefined
   */
  defaultValue?: string;
  /**
   * Return the value at every change
   * @default undefined
   */
  onChange?: (value: string) => void;
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  /**
   * Key events
   */
  onKeyPress?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onKeyUp?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  /**
   * Display green color if true
   * @default false
   */
  success?: boolean;
  /**
   * Display red color if true
   * @default false
   */
  error?: boolean;
  /**
   * Display in bottom
   * @default undefined
   */
  helperText?: string | null;
  /**
   * Display textarea in disabled mode
   */
  disabled?: boolean;
  /**
   * if true, hide label text when user click or when text is filled
   * @default false
   */
  hideLabelOnClick?: boolean;
  sx?: SxProps;
  /**
   * @default default
   */
  variant?: "default" | "chat";
  onInput?: (e: any) => void;
  nonce?: string;
  /**
   * Prevent scrolling when textarea is focused
   * @default false
   */
  preventScroll?: boolean;
  inputRef?: React.RefObject<HTMLTextAreaElement>;
  /**
   * Empêche Enter de déclencher un "send"/submit en stoppant la propagation
   * tout en laissant le navigateur insérer la nouvelle ligne.
   * @default true
   */
  preventSubmitOnEnter?: boolean;
  /**
   * Indice pour les claviers mobiles (Android/iOS) sur le libellé de la touche.
   * @default 'enter'
   */
  enterKeyHint?:
    | "enter"
    | "done"
    | "go"
    | "next"
    | "previous"
    | "search"
    | "send";
};

const StyledTextarea = styled("textarea")((props) => {
  const { theme }: { theme: any } = props;
  return {
    fontFamily: "Nunito Sans",
    border: `1px solid ${theme?.palette.greyGradient[100]}`,
    fontSize: "14px",
    fontWeight: "bold",
    resize: "none",
    color: theme.palette.greyGradient.main,
    borderRadius: "16px",
    padding: "10px",
    lineHeight: "1",
    "&:focus-visible": {
      outline: "none",
      borderColor: theme.palette.greyGradient[250],
    },
    "&.success": {
      borderColor: theme.palette.success.light,
      "&:focus-visible": {
        borderColor: theme.palette.success[250],
      },
    },
    "&.error": {
      borderColor: theme.palette.error.light,
      "&:focus-visible": {
        borderColor: theme.palette.error[250],
      },
    },
    "&:disabled": {
      borderColor: theme.palette.greyGradient[100],
    },
    "&::-webkit-scrollbar": {
      width: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.greyGradient[150],
      borderRadius: "8px",
    },
    scrollbarColor: `${theme.palette.greyGradient[150]} transparent`,
  };
});

const TextareaWrapper = styled("div")(({ theme }: { theme?: EmtTheme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  // border: `1px solid ${theme?.palette.greyGradient[100]}`,
  borderRadius: "16px",
  background: theme?.palette.common.white,
  "& .emt-maxlength": {
    color: theme?.palette.greyGradient[250],
    position: "absolute",
    top: "10px",
    right: "10px",
  },
  "& .emt-label": {
    color: theme?.palette.greyGradient[250],
    position: "absolute",
    top: "10px",
    left: "10px",
  },
  "&.disabled": {
    borderColor: theme?.palette.greyGradient[100],
    background: theme?.palette.greyGradient[100],
  },
}));

const StyledChatTextarea = styled(StyledTextarea as any)(
  (props: { isMobile?: boolean }) => {
    return {
      borderRadius: 0,
      padding: 0,
      paddingTop: "5px",
      height: props.isMobile ? "23px" : "auto",
    };
  }
);

const ChatTextareaWrapper = styled(TextareaWrapper as any)(
  ({ theme, isMobile }: { theme?: EmtTheme; isMobile?: boolean }) => ({
    background: theme?.palette.common.white,
    padding: "5px 10px",
    paddingRight: "20px",
    "& .emt-label": {
      top: isMobile ? "10px" : "14px",
    },
  })
);

export function EmtTextarea(props: EmtTextareaProps) {
  const {
    defaultValue,
    value,
    disabled,
    helperText,
    label,
    success,
    error,
    onChange,
    hideLabelOnClick = false,
    sx,
    variant = "default",
    onInput,
    preventScroll = false,
    onFocus,
    inputRef,
    preventSubmitOnEnter = true,
    enterKeyHint = "enter",
    ...rest
  } = props;

  const [isMobile] = useMobile();

  const [valueLength, setValueLength] = useState(
    value?.length || defaultValue?.length || 0
  );
  const [showLabel, setShowLabel] = useState(
    (value || defaultValue) && hideLabelOnClick ? false : true
  );
  const ref = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) =>
      ref.current &&
      !ref.current?.contains(event.target as any) &&
      valueLength <= 0
        ? setShowLabel(true)
        : null;

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, valueLength]);

  useEffect(() => {
    setValueLength(value?.length || defaultValue?.length || 0);
    setShowLabel((value || defaultValue) && hideLabelOnClick ? false : true);
  }, [defaultValue, value]);

  const autoGrow = (e: any) => {
    const target = e.target || e;
    if (isMobile && variant === "chat") {
      target.style.height = "23px";
      const scrollHeight = target.scrollHeight;
      target.style.height = scrollHeight > 23 ? scrollHeight + "px" : "23px";
    } else if (variant === "chat") {
      const height = !target.value?.length
        ? "auto"
        : target.scrollHeight + "px";
      target.style.height = height;
    }
  };

  const handleOnChange = (event: any) => {
    setValueLength(event.target.value?.length || 0);
    autoGrow(event);
    onChange ? onChange(event.target.value) : null;
  };

  useEffect(() => {
    if (inputRef?.current) {
      if (isMobile && variant === "chat") {
        // Set initial height for mobile
        inputRef.current.style.height = "23px";
        const scrollHeight = inputRef.current.scrollHeight;
        inputRef.current.style.height =
          scrollHeight > 23 ? scrollHeight + "px" : "23px";
      } else {
        autoGrow({ target: inputRef.current });
      }
    }
  }, [inputRef, isMobile, variant, value]);

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    if (preventScroll) {
      e.preventDefault();
      e.target.scrollIntoView({ block: "nearest" });
    }
    onFocus?.(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      if (e.shiftKey) {
        e.stopPropagation();
        autoGrow(e);
        return;
      }

      if (preventSubmitOnEnter) {
        e.stopPropagation();
        autoGrow(e);
        return;
      }
    }

    if (typeof rest?.onKeyDown === "function") rest.onKeyDown(e);
  };

  const colorClassName = disabled
    ? "disabled"
    : success
    ? "success"
    : error
    ? "error"
    : "";

  return variant === "chat" ? (
    <ChatTextareaWrapper ref={ref} isMobile={isMobile}>
      <StyledChatTextarea
        ref={inputRef}
        isMobile={isMobile}
        disabled={disabled}
        className={colorClassName}
        value={value}
        defaultValue={defaultValue}
        onChange={handleOnChange}
        onClick={() => setShowLabel(!hideLabelOnClick)}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        enterKeyHint={enterKeyHint}
        sx={{ ...sx }}
        onInput={(e: any) => {
          autoGrow(e);
          onInput ? onInput(e) : null;
        }}
        {...rest}
      />
      {props.maxLength && (
        <EmtTypography variant="body2" className="emt-maxlength">
          {valueLength}/{props.maxLength}
        </EmtTypography>
      )}
      {showLabel && (
        <EmtTypography
          variant="caption"
          className="emt-label"
          onClick={() => {
            ref.current
              ? (ref.current.firstChild as HTMLElement)?.focus()
              : null;
            setShowLabel(!hideLabelOnClick);
          }}
          sx={{ zIndex: "0", paddingRight: props.maxLength ? "60px" : 0 }}
        >
          {label}
        </EmtTypography>
      )}
    </ChatTextareaWrapper>
  ) : (
    <Box>
      <TextareaWrapper ref={ref} className={props.disabled ? "disabled" : ""}>
        <StyledTextarea
          ref={inputRef}
          disabled={disabled}
          className={colorClassName}
          defaultValue={defaultValue}
          value={value}
          onChange={handleOnChange}
          onClick={() => setShowLabel(!hideLabelOnClick)}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          enterKeyHint={enterKeyHint}
          sx={{
            paddingTop: hideLabelOnClick ? "8px" : "25px",
            paddingRight: props.maxLength ? "60px" : "10px",
            ...sx,
            // height: isMobile ? "23px" : "auto",
          }}
          {...rest}
        />
        {props.maxLength && (
          <EmtTypography variant="caption" className="emt-maxlength">
            {valueLength}/{props.maxLength}
          </EmtTypography>
        )}
        {showLabel && (
          <EmtTypography
            variant="caption"
            className="emt-label"
            onClick={() => {
              ref.current
                ? (ref.current.firstChild as HTMLElement)?.focus()
                : null;
              setShowLabel(!hideLabelOnClick);
            }}
            sx={{ zIndex: "0", paddingRight: props.maxLength ? "60px" : 0 }}
          >
            {label}
          </EmtTypography>
        )}
      </TextareaWrapper>

      {helperText && (
        <FormHelperText
          color={colorClassName}
          sx={{
            paddingTop: "5px",
            fontFamily: "Nunito Sans, Helvetica, Arial, sans-serif!important",
          }}
        >
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
}
